import React, { useState } from 'react';
import Hero from '../../components/Hero/Hero';
import Pathfinder from '../../components/Pathfinder/Pathfinder';
import Metadata from '../../components/Metadata/Metadata';
import { Box, Typography } from '@mui/material';
import GlobalCallout from '../../components/Global/CalloutDefault';
import Card from '../../components/Card/Card';
import Section from '../../components/Section/Section';
import Accordion from '../../components/Accordion/Accordion';
import Wallpaper from '../../components/Wallpaper/Wallpaper';
import Image from '../../components/Image/Image';
import ContentSplit from '../../components/ContentSplit/ContentSplit';
import ListItem from '../../components/ListItem/ListItem';
import Button from '../../components/Button/Button';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };
  return (
    <Box>
      <Metadata
        title="How It Works | General Genomics"
        description="Learn how to navigate General Genomics' Data Portal with step-by-step guidance. Discover how to access data and leverage its features for tailored solutions across industries."
      />
      <Hero
        helper="Data Portal"
        headline="Discover how to use our data portal for insights"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Unlock the Power of Data
        </h2>
        <p className="action-on-view action-fade-in">
          Our Data Portal simplifies data access, making it easier for you to
          navigate complex datasets, integrate secure compliance measures, and
          leverage tailored solutions across industries. Explore how to easily
          request data, browse interactive features, and ensure secure access,
          all in a user-friendly environment.
        </p>
        <Image
          path="/images/library/office-man-woman.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/military-mechanic-man-repair-7.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">How It Works</p>
        <h2 className="action-on-view action-fade-in">
          Effortless Data Access and Security
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Step-by-Step Guide:</strong> Navigate through the portal
            easily, access relevant data, and seamlessly request tailored
            solutions that meet your unique needs.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Secure Data Access:</strong> Our platform ensures that all
            data is accessed securely, complying with industry standards for the
            highest level of protection.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>User-Friendly Interface:</strong> The portal is designed
            with ease-of-use in mind, helping you access and request data
            quickly without complex training.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Cross-Sector Solutions:</strong> From healthcare to finance,
            the Data Portal provides tailored insights for various industries to
            enhance decision-making and innovation.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More of Our Solutions
        </h2>
        <p className="action-on-view action-fade-in">
          Discover a range of resources and offerings that enhance your
          understanding of our platform and data solutions.
        </p>
        <div className="card-group">
          <Card
            icon="account_tree"
            headline="Data Portal"
            subhead="Gain a broader overview of our available data solutions."
            btnURL="/data-portal"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="lock"
            headline="Data Governance & Compliance"
            subhead="Ensure your data solutions meet the highest standards of security and compliance."
            btnURL="/our-approach/data-governance-and-compliance"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="cloud_upload"
            headline="Synthetic & De-Identified Data"
            subhead="Explore the types of secure, anonymized data available for your use."
            btnURL="/innovation/syn46"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../images/library/military-mechanic-group-tablet.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about General Genomics&apos; Data Portal
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Find answers on navigating, accessing, and utilizing General
          Genomics&apos; Data Portal for secure, tailored data solutions across
          industries.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="How do I access data through the General Genomics Data Portal?"
          >
            To access data, simply follow the step-by-step guide provided on the
            portal. You&apos;ll be able to browse and request tailored data
            solutions after logging in securely.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Is the General Genomics Data Portal compliant with industry standards?"
          >
            Yes, the portal is designed with robust data governance and
            compliance features, ensuring secure data access and integration
            with industry standards.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What types of data are available through the Data Portal?"
          >
            The portal offers a range of data types, including synthetic data
            (SYN46™) and de-identified data, which can be explored in more
            detail on the respective pages.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How does General Genomics ensure the security of the data on the portal?"
          >
            Security is a top priority, and the portal integrates advanced
            compliance and security measures to protect your data and ensure
            privacy throughout its use.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
